// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, ChevronDown, Loader2, Search } from 'lucide-react';
import { getAllClients } from '../services/api';
import { aaiApi } from '../services/aaiApi';
import { useOpenWorkspace } from '../hooks/useOpenWorkspace';
import { toast } from 'react-toastify';

/**
 * One Identity P5.3 — the context switcher (customer edition).
 *
 * The customer's whole cross-app story is one button, always top-right:
 * their company, and "Open workspace" — which mints a single-use crossing
 * ticket and lands them in their builder workspace as themselves. Staff get
 * the fuller pill (client picker + app toggle) in the staff shell.
 *
 * This control retires BuilderWorkspaceCard and the scattered per-page
 * deep-link buttons as the way across.
 */
// The name for the place a client crosses into — where their agents are built
// (by the planner) and operate. Orbit scopes the new agent; the planner takes
// it onward; this is its home. One constant so the name is easy to settle.
const WORKSPACE_NAME = 'Workspace';

/** Customer edition: their company + one button. */
const CustomerSwitcher = () => {
    const { opening, open } = useOpenWorkspace();
    const companyName = localStorage.getItem('client_name') || '';
    const clientId = localStorage.getItem('client_id');
    if (!clientId) return null;

    return (
        <div className="flex items-center gap-2 bg-white/95 backdrop-blur border border-gray-200 rounded-full pl-1.5 pr-1.5 py-1.5 shadow-sm sm:pl-4">
            {companyName && (
                <span className="hidden text-sm font-medium text-gray-800 max-w-[160px] truncate sm:block" title={companyName}>
                    {companyName}
                </span>
            )}
            <button
                onClick={() => open(clientId)}
                disabled={opening}
                title={`Open ${WORKSPACE_NAME}`}
                className="flex items-center gap-2 bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold rounded-full pl-1.5 pr-1.5 py-1 transition-colors disabled:opacity-60 sm:pr-4"
            >
                <span className="grid place-items-center w-6 h-6 rounded-full bg-gray-900 shrink-0">
                    {opening ? <Loader2 size={12} className="animate-spin text-white" /> : <img src="/icon-white.png" alt="" className="w-3.5 h-3.5 object-contain" />}
                </span>
                <span className="hidden sm:inline">{opening ? 'Opening…' : `Open ${WORKSPACE_NAME}`}</span>
            </button>
        </div>
    );
};

/** Provider edition: a provider company's Owner opens their own workspace or
 *  any client's they serve, the way staff pick one. */
const ProviderSwitcher = () => {
    const { opening, open } = useOpenWorkspace();
    const [clients, setClients] = useState([]);
    const [openList, setOpenList] = useState(false);
    const [q, setQ] = useState('');
    const boxRef = useRef(null);
    const companyName = localStorage.getItem('client_name') || '';
    const clientId = localStorage.getItem('client_id');

    useEffect(() => {
        aaiApi.getPartnerOverview().then((res) => setClients(res.data?.clients || [])).catch(() => setClients([]));
    }, []);

    useEffect(() => {
        const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpenList(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    if (!clientId) return null;
    const go = (id) => { setOpenList(false); setQ(''); open(id); };
    const needle = q.trim().toLowerCase();
    const filtered = needle
        ? clients.filter((c) => (c.name || '').toLowerCase().includes(needle) || (c.contact_email || '').toLowerCase().includes(needle))
        : clients;
    const ownMatches = !needle || (companyName || 'your company').toLowerCase().includes(needle);

    return (
        <div ref={boxRef} className="relative">
            <button onClick={() => setOpenList(!openList)} disabled={opening}
                className="flex items-center gap-2 bg-white/95 backdrop-blur border border-gray-200 rounded-full pl-3 pr-2.5 h-9 text-sm font-medium text-gray-700 shadow-sm hover:border-brand-200 disabled:opacity-60 sm:pl-4 sm:pr-3">
                {opening ? <Loader2 size={14} className="animate-spin text-brand-500" /> : <ArrowUpRight size={14} className="text-brand-500" />}
                <span className="hidden sm:inline">{opening ? `Opening ${WORKSPACE_NAME}…` : `Open ${WORKSPACE_NAME}`}</span>
                <ChevronDown size={14} className="text-gray-400 shrink-0" />
            </button>
            {openList && (
                <div className="absolute top-full right-0 mt-2 w-72 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-[90]">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
                        <Search size={14} className="text-gray-400" />
                        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                            placeholder="Search clients…"
                            aria-label="Search clients"
                            className="flex-1 text-sm outline-none bg-transparent text-gray-900 placeholder:text-gray-400" />
                    </div>
                    {ownMatches && (
                        <button onClick={() => go(clientId)}
                            className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 flex items-center justify-between gap-2 border-b border-gray-100 group">
                            <span className="min-w-0">
                                <span className="block font-medium text-gray-800 truncate">{companyName || 'Your company'}</span>
                                {companyName && <span className="block text-xs text-gray-400">Your company</span>}
                            </span>
                            <ArrowUpRight size={14} className="text-gray-300 group-hover:text-brand-500 shrink-0" />
                        </button>
                    )}
                    <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Your clients</div>
                    <div className="max-h-72 overflow-y-auto">
                        {filtered.map((c) => (
                            <button key={c.id} onClick={() => go(c.id)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between gap-2 group">
                                <span className="block min-w-0 font-medium text-gray-800 truncate">{c.name}</span>
                                <ArrowUpRight size={14} className="text-gray-300 group-hover:text-brand-500 shrink-0" />
                            </button>
                        ))}
                        {clients.length === 0 && (
                            <div className="px-3 py-3 text-sm text-gray-400">No clients yet. Add one from Clients.</div>
                        )}
                        {clients.length > 0 && filtered.length === 0 && (
                            <div className="px-3 py-3 text-sm text-gray-400">No client matches "{q}".</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

/** Staff edition: "Working on <client> ▾" picker + open-in-builder for the
 *  selected client. Consultants and supers cross AS THEMSELVES (the ticket
 *  crossing records the real person). */
const StaffSwitcher = () => {
    const { opening, open } = useOpenWorkspace();
    const [clients, setClients] = useState([]);
    const [openList, setOpenList] = useState(false);
    const [q, setQ] = useState('');
    const boxRef = useRef(null);

    useEffect(() => {
        (async () => {
            try { setClients(await getAllClients() || []); } catch { /* staff list optional */ }
        })();
    }, []);

    useEffect(() => {
        const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpenList(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const filtered = q
        ? clients.filter((c) => (c.name || '').toLowerCase().includes(q.toLowerCase()) || (c.email || '').toLowerCase().includes(q.toLowerCase()))
        : clients;

    // Pick a client → go STRAIGHT to their workspace (crossing ticket). No
    // client-detail detour, no two-step. Search, click, you're in the builder.
    const goToWorkspace = (c) => { setOpenList(false); open(c.id); };

    return (
        <div ref={boxRef} className="relative">
            <button onClick={() => setOpenList(!openList)} disabled={opening}
                className="flex items-center gap-2 bg-white/95 backdrop-blur border border-gray-200 rounded-full pl-3 pr-2.5 h-9 text-sm font-medium text-gray-700 shadow-sm hover:border-brand-200 disabled:opacity-60 sm:pl-4 sm:pr-3">
                {opening ? <Loader2 size={14} className="animate-spin text-brand-500" /> : <ArrowUpRight size={14} className="text-brand-500" />}
                <span className="hidden sm:inline">{opening ? `Opening ${WORKSPACE_NAME}…` : `Open a ${WORKSPACE_NAME}`}</span>
                <ChevronDown size={14} className="text-gray-400 shrink-0" />
            </button>

            {openList && (
                <div className="absolute top-full right-0 mt-2 w-72 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-[90]">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
                        <Search size={14} className="text-gray-400" />
                        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                            placeholder="Search clients…"
                            className="flex-1 text-sm outline-none bg-transparent text-gray-900 placeholder:text-gray-400" />
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                        {filtered.map((c) => (
                            <button key={c.id}
                                onClick={() => goToWorkspace(c)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between gap-2 group">
                                <span className="min-w-0">
                                    <span className="block font-medium text-gray-800 truncate">{c.name}</span>
                                    <span className="block text-xs text-gray-400 truncate">{c.email}</span>
                                </span>
                                <ArrowUpRight size={14} className="text-gray-300 group-hover:text-brand-500 shrink-0" />
                            </button>
                        ))}
                        {filtered.length === 0 && (
                            <div className="px-3 py-4 text-sm text-gray-400 text-center">No matches</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const ContextSwitcher = ({ variant = 'client' }) => {
    if (variant === 'staff') return <StaffSwitcher />;
    if (localStorage.getItem('provider_id') === localStorage.getItem('client_id') && localStorage.getItem('provider_id')) return <ProviderSwitcher />;
    return <CustomerSwitcher />;
};

export default ContextSwitcher;
